import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  AutoAwesome as AIIcon,
  Save as SaveIcon,
  PlayArrow as StartIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useSaveGeneratedOpinion } from "../hooks/useAIGenerator";
import { generateOpinion } from "../services/api";
import {
  GenerateOpinionRequest,
  GENRES,
  DIFFICULTIES,
  DIFFICULTY_LABELS,
  GENRE_LABELS,
  Genre,
  Opinion,
} from "../types";
import TopicStatsTable from "./TopicStatsTable";
import DifficultyChip from "./common/DifficultyChip";

interface GenerationResult {
  index: number;
  genre: string;
  difficulty: number;
  status: "pending" | "generating" | "completed" | "error";
  opinion?: Opinion;
  error?: string;
  selected?: boolean;
}

interface GenreConfig {
  genre: string;
  count: number;
}

const BulkAdd: React.FC = () => {
  const [note, setNote] = useState("");
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [difficulty, setDifficulty] = useState<number>(3);
  const [genreConfigs, setGenreConfigs] = useState<GenreConfig[]>([]);

  const [generationResults, setGenerationResults] = useState<GenerationResult[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [selectedOpinions, setSelectedOpinions] = useState<Set<number>>(new Set());
  const [previewDialog, setPreviewDialog] = useState<{ open: boolean; opinion?: Opinion }>({
    open: false,
  });

  const saveMutation = useSaveGeneratedOpinion();

  const handleBulkGenerate = async () => {
    if (genreConfigs.length === 0 || !difficulty) {
      return;
    }

    setIsGenerating(true);
    setCurrentProgress(0);

    // 全ジャンル・問題数の総計を計算
    const totalCount = genreConfigs.reduce((sum, config) => sum + config.count, 0);

    // 各ジャンルの問題をフラット化したリストを作成
    const initialResults: GenerationResult[] = [];
    let index = 1;
    genreConfigs.forEach((config) => {
      for (let i = 0; i < config.count; i++) {
        initialResults.push({
          index: index++,
          genre: config.genre,
          difficulty,
          status: "pending",
        });
      }
    });

    setGenerationResults(initialResults);

    // 生成済みテーマをローカル変数で管理（状態更新の遅延を避けるため）
    const collectedThemes: string[] = [];

    // 1件ずつ順番に生成
    for (let i = 0; i < totalCount; i++) {
      const currentTask = initialResults[i];
      try {
        // 現在の進捗状況を更新
        setGenerationResults((prev) =>
          prev.map((result, idx) => (idx === i ? { ...result, status: "generating" } : result))
        );

        // 生成済みテーマをnoteに追加して重複防止
        let requestNote = note.trim();

        // 常に多様性を促す指示を追加
        if (i > 0) {
          requestNote =
            requestNote +
            `\n\n【重要】これは${totalCount}問中${i + 1}問目の生成です（ジャンル: ${currentTask.genre}）。多様性を確保するため、前回とは全く異なる視点やアプローチで作成してください。`;
        }

        // 同じジャンルの生成済みテーマを特に強調
        const sameGenreThemes = collectedThemes.filter(
          (_, idx) => initialResults[idx] && initialResults[idx].genre === currentTask.genre
        );
        if (sameGenreThemes.length > 0) {
          requestNote =
            requestNote +
            `\n\n【特に重要】同じジャンル「${currentTask.genre}」では以下のテーマが既に生成済みです。これらとは全く違う角度から作成してください：\n${sameGenreThemes.map((t) => `・${t}`).join("\n")}`;
        }

        if (collectedThemes.length > 0) {
          const excludeNote = `\n\n【全般的禁止事項】以下の全テーマは既に生成済みです。これらと同じ、類似、関連するテーマは絶対に避けてください：\n${collectedThemes.map((t) => `・${t}`).join("\n")}`;
          requestNote = requestNote + excludeNote;
        }

        const request: GenerateOpinionRequest = {
          genre: currentTask.genre,
          difficulty: currentTask.difficulty,
          note: requestNote || undefined,
        };

        // 単一のAI生成APIを呼び出し
        const generatedOpinion = await generateOpinion(request);

        // 成功した結果を更新
        setGenerationResults((prev) =>
          prev.map((result, idx) =>
            idx === i
              ? {
                  ...result,
                  status: "completed",
                  opinion: generatedOpinion,
                }
              : result
          )
        );

        // 生成されたテーマをローカル配列で追跡
        if (generatedOpinion.theme) {
          collectedThemes.push(generatedOpinion.theme);
        }

        // プログレスバーを更新
        setCurrentProgress(((i + 1) / totalCount) * 100);
      } catch (error) {
        console.error(`Failed to generate opinion ${i + 1}:`, error);

        // エラーの結果を更新
        setGenerationResults((prev) =>
          prev.map((result, idx) =>
            idx === i
              ? {
                  ...result,
                  status: "error",
                  error: (error as Error).message,
                }
              : result
          )
        );
      }
    }

    setIsGenerating(false);
  };

  const stats = {
    total: generationResults.length,
    completed: generationResults.filter((r) => r.status === "completed").length,
    error: generationResults.filter((r) => r.status === "error").length,
  };

  // 選択されたOpinionの切り替え
  const toggleOpinionSelection = (index: number) => {
    const newSelected = new Set(selectedOpinions);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedOpinions(newSelected);
  };

  // 全選択/全解除
  const toggleAllSelection = () => {
    const completedResults = generationResults.filter((r) => r.status === "completed");
    if (selectedOpinions.size === completedResults.length) {
      setSelectedOpinions(new Set());
    } else {
      setSelectedOpinions(new Set(completedResults.map((r) => r.index)));
    }
  };

  // 選択された生成結果を保存
  const handleSaveSelected = async () => {
    const selectedResults = generationResults.filter(
      (r) => r.status === "completed" && r.opinion && selectedOpinions.has(r.index)
    );

    for (const result of selectedResults) {
      if (result.opinion) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id, createdAt, updatedAt, ...opinionData } = result.opinion;
          await saveMutation.mutateAsync(opinionData);
        } catch (error) {
          console.error(`Failed to save opinion ${result.index}:`, error);
        }
      }
    }

    // 保存後、結果をクリア
    setGenerationResults([]);
    setCurrentProgress(0);
    setSelectedOpinions(new Set());
  };

  // プレビューダイアログを開く
  const handlePreviewOpinion = (opinion: Opinion) => {
    setPreviewDialog({ open: true, opinion });
  };

  // プレビューダイアログを閉じる
  const handleClosePreview = () => {
    setPreviewDialog({ open: false });
  };

  // ジャンル選択のハンドル
  const handleGenreSelection = (genre: string) => {
    setSelectedGenres((prev) => {
      if (prev.includes(genre)) {
        // 選択解除時は設定も削除
        setGenreConfigs((configs) => configs.filter((c) => c.genre !== genre));
        return prev.filter((g) => g !== genre);
      } else {
        // 新規選択時はデフォルトで、3問設定
        setGenreConfigs((configs) => [...configs, { genre, count: 3 }]);
        return [...prev, genre];
      }
    });
  };

  // ジャンル別問題数の更新
  const handleGenreCountChange = (genre: string, count: number) => {
    setGenreConfigs((configs) =>
      configs.map((c) =>
        c.genre === genre ? { ...c, count: Math.max(1, Math.min(10, count)) } : c
      )
    );
  };

  // 全体の統計情報
  const totalQuestions = genreConfigs.reduce((sum, config) => sum + config.count, 0);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">一括問題生成</Typography>
        {stats.total > 0 && (
          <Box display="flex" gap={1} alignItems="center">
            <Chip label={`生成完了: ${stats.completed}件`} color="success" variant="outlined" />
            <Chip label={`エラー: ${stats.error}件`} color="error" variant="outlined" />
            {stats.completed > 0 && !isGenerating && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<SaveIcon />}
                onClick={handleSaveSelected}
                disabled={saveMutation.isLoading}
              >
                {saveMutation.isLoading ? "保存中..." : `全て保存 (${stats.completed}件)`}
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ position: "sticky", top: 24 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                一括生成設定
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="備考（任意）"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  fullWidth
                  placeholder="例: 環境問題について"
                  helperText="生成したい問題の補足情報を入力してください（任意）"
                  disabled={isGenerating}
                  multiline
                  rows={2}
                />

                <Typography variant="subtitle1" gutterBottom>
                  ジャンル選択 *
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {GENRES.map((g) => (
                    <FormControlLabel
                      key={g}
                      control={
                        <Checkbox
                          checked={selectedGenres.includes(g)}
                          onChange={() => handleGenreSelection(g)}
                          disabled={isGenerating}
                        />
                      }
                      label={g}
                      sx={{ display: "block", mb: 0.5 }}
                    />
                  ))}
                </Box>

                {/* 選択されたジャンルの問題数設定 */}
                {genreConfigs.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      ジャンル別問題数設定
                    </Typography>
                    {genreConfigs.map((config) => (
                      <Box
                        key={config.genre}
                        sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body2" sx={{ minWidth: 120, fontSize: "0.875rem" }}>
                          {GENRE_LABELS[config.genre as Genre] || config.genre}
                        </Typography>
                        <TextField
                          type="number"
                          value={config.count}
                          onChange={(e) =>
                            handleGenreCountChange(config.genre, Number(e.target.value))
                          }
                          size="small"
                          sx={{ width: 80 }}
                          inputProps={{ min: 1, max: 10 }}
                          disabled={isGenerating}
                        />
                        <Typography variant="caption" color="text.secondary">
                          問
                        </Typography>
                      </Box>
                    ))}
                    <Typography variant="caption" color="primary" sx={{ fontWeight: "bold" }}>
                      合計: {totalQuestions}問
                    </Typography>
                  </Box>
                )}

                <FormControl fullWidth>
                  <InputLabel>難易度 *</InputLabel>
                  <Select
                    value={difficulty}
                    onChange={(e) => setDifficulty(Number(e.target.value))}
                    label="難易度 *"
                    disabled={isGenerating}
                  >
                    {DIFFICULTIES.map((diff) => (
                      <MenuItem key={diff} value={diff}>
                        難易度{diff} - {DIFFICULTY_LABELS[diff]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={isGenerating ? <CircularProgress size={20} /> : <StartIcon />}
                  onClick={handleBulkGenerate}
                  disabled={genreConfigs.length === 0 || !difficulty || isGenerating}
                  fullWidth
                >
                  {isGenerating ? "一括生成中..." : `${totalQuestions}問一括生成`}
                </Button>

                <Divider sx={{ my: 2 }} />

                {/* 統計情報 */}
                <TopicStatsTable />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 8 }}>
          <>
            {saveMutation.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                保存に失敗しました: {(saveMutation.error as Error).message}
              </Alert>
            )}

            {isGenerating && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <CircularProgress size={24} />
                    <Typography variant="h6">問題を一括生成中...</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={currentProgress} sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary">
                    {stats.completed}/{stats.total} 完了
                  </Typography>
                </CardContent>
              </Card>
            )}

            {generationResults.length === 0 && !isGenerating && (
              <Paper sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
                <AIIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                  一括生成結果がここに表示されます
                </Typography>
                <Typography variant="body2">
                  左の設定からジャンル・難易度・生成数を選択して一括生成を実行してください
                </Typography>
              </Paper>
            )}

            {generationResults.length > 0 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">生成結果一覧 ({stats.completed}件完了)</Typography>
                  {stats.completed > 0 && (
                    <Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={toggleAllSelection}
                        sx={{ mr: 1 }}
                      >
                        {selectedOpinions.size === stats.completed ? "全解除" : "全選択"}
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveSelected}
                        disabled={selectedOpinions.size === 0 || saveMutation.isLoading}
                      >
                        選択した{selectedOpinions.size}件を保存
                      </Button>
                    </Box>
                  )}
                </Box>

                <List>
                  {generationResults.map((result, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        {result.status === "completed" && <CheckIcon color="success" />}
                        {result.status === "error" && <ErrorIcon color="error" />}
                        {result.status === "generating" && <CircularProgress size={20} />}
                        {result.status === "pending" && <CircularProgress size={20} />}
                      </ListItemIcon>
                      <ListItemText
                        primary={`問題 ${result.index}`}
                        secondary={
                          result.status === "error"
                            ? `エラー: ${result.error}`
                            : result.status === "completed"
                              ? "生成完了"
                              : "生成中..."
                        }
                      />
                    </ListItem>
                  ))}
                </List>

                {stats.completed > 0 && (
                  <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                      生成された問題の詳細
                    </Typography>
                    <Grid container spacing={2}>
                      {generationResults
                        .filter((result) => result.status === "completed" && result.opinion)
                        .map((result) => (
                          <Grid size={{ xs: 12, md: 6 }} key={result.index}>
                            <Card variant="outlined" sx={{ position: "relative" }}>
                              <CardContent>
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="flex-start"
                                  mb={1}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={selectedOpinions.has(result.index)}
                                        onChange={() => toggleOpinionSelection(result.index)}
                                      />
                                    }
                                    label={`問題 ${result.index}`}
                                  />
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handlePreviewOpinion(result.opinion!)}
                                  >
                                    詳細
                                  </Button>
                                </Box>

                                <Box display="flex" gap={1} mb={1}>
                                  <DifficultyChip
                                    difficulty={result.opinion!.difficulty}
                                    size="small"
                                    variant="outlined"
                                  />
                                  <Chip
                                    label={
                                      GENRE_LABELS[result.opinion!.genre as Genre] ||
                                      result.opinion!.genre
                                    }
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                  />
                                </Box>

                                {/* テーマ */}
                                {result.opinion!.theme && (
                                  <Typography variant="h6" gutterBottom sx={{ fontSize: "1rem" }}>
                                    {result.opinion!.theme}
                                  </Typography>
                                )}

                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {result.opinion!.sourceText}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                )}

                {!isGenerating && (stats.error > 0 || stats.completed > 0) && (
                  <Box mt={3} display="flex" justifyContent="center">
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setGenerationResults([]);
                        setSelectedOpinions(new Set());
                        setCurrentProgress(0);
                      }}
                    >
                      新しい一括生成を開始
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </>
        </Grid>
      </Grid>

      {/* プレビューダイアログ */}
      <Dialog open={previewDialog.open} onClose={handleClosePreview} maxWidth="md" fullWidth>
        <DialogTitle>問題プレビュー</DialogTitle>
        <DialogContent>
          {previewDialog.opinion && (
            <Box>
              <Box display="flex" gap={1} mb={2}>
                <DifficultyChip
                  difficulty={previewDialog.opinion.difficulty}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={
                    GENRE_LABELS[previewDialog.opinion.genre as Genre] ||
                    previewDialog.opinion.genre
                  }
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
                <Chip label="AI生成" size="small" color="info" variant="outlined" />
              </Box>

              {previewDialog.opinion.theme && (
                <Typography variant="h5" gutterBottom>
                  {previewDialog.opinion.theme}
                </Typography>
              )}

              <Typography variant="body1" paragraph>
                {previewDialog.opinion.sourceText}
              </Typography>

              {previewDialog.opinion.opinionPatterns &&
                previewDialog.opinion.opinionPatterns.length > 0 && (
                  <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                      意見パターン
                    </Typography>
                    {previewDialog.opinion.opinionPatterns.map((pattern, index) => (
                      <Box key={index} mb={2}>
                        <Typography variant="subtitle2" color="primary">
                          {pattern.typeLabel}
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {pattern.example}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pattern.description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkAdd;
